/**
 * Dice reference charts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tabletop_iii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const diceReferenceChartsPack: ToilePack = {
    slug: 'dice-reference-charts',
    category: 'tabletop',
    name: 'Dice reference charts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'dice', label: 'Dice set', subject: 'a chart showing the seven polyhedral dice drawn in a row, each labelled "d4", "d6", "d8", "d10", "d%", "d12" and "d20" with its face count beneath'},
        {key: 'notation', label: 'Dice notation', subject: 'a chart titled "Reading a Roll" with three worked lines "1d20", "2d6" and "1d8 + 3", each beside a small drawing of the dice it means'},
        {key: 'results', label: 'd20 results', subject: 'a chart titled "d20" with a d20 drawn at the top and a ruled table below with rows "1 Critical fail", "2 to 19 Add your bonus" and "20 Critical hit"'},
    ],
    styles: [
        {key: 'flat', label: 'Flat dice', style: 'crisp flat vector dice with clean outlines and bold labels', palette: 'bright jewel colors of red, blue, green and purple on white', framing: 'centered on the page inside a wide margin'},
        {key: 'blueprint', label: 'Blueprint', style: 'a technical blueprint drawing with fine white linework and measurement ticks', palette: 'white line on deep blue', framing: 'centered on the page inside a wide margin'},
        {key: 'parchment', label: 'Parchment', style: 'a hand-inked guide on aged parchment with a decorative border', palette: 'sepia ink on warm tan paper', framing: 'centered on the page inside a wide margin'},
    ],
    directions: ['metal dice', 'cheat sheet', 'for kids'],
    directionHint: 'try: dice set',
    seo: {
        title: 'Dice reference charts: dice set, notation, d20',
        metaDescription: 'Printable dice reference charts in three styles: the polyhedral dice set labelled, a dice notation guide and a d20 result table.',
        h1: 'Dice reference charts: dice set, dice notation, d20 results',
        tagline: 'Nine charts: the labelled dice set, a notation guide and a d20 table, in three styles.',
        intro: 'New players pin a dice chart up to learn which die is which and how to read a roll. This pack shows the seven polyhedral dice labelled d4 to d20, a Reading a Roll chart with worked lines such as 1d20 and 2d6, and a d20 result table. Each comes as flat bright dice, a technical blueprint or an old parchment guide. Type a subject, such as metal dice, and the chart keeps its labels.',
        useCases: [
            'a chart pinned up for a new player',
            'a notation guide at the game table',
            'a d20 result table for a game master',
            'a reference for a class learning dice',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'Three readings in three styles: the labelled dice set, a dice notation guide, and a d20 result table.'},
            {q: 'Which dice are shown?', a: 'Seven polyhedral dice: d4, d6, d8, d10, d%, d12 and d20, each with its face count beneath.'},
            {q: 'What does the notation guide explain?', a: 'How to read a roll such as 1d20, 2d6 or 1d8 + 3, each beside a small drawing of the dice it means.'},
            {q: 'What does the d20 table show?', a: 'A ruled table with three rows: 1 for a critical fail, 2 to 19 to add your bonus, and 20 for a critical hit.'},
        ],
    },
}
